import { useState } from 'react';
import { useUser } from './user';
import { useCloset } from './closet';
import { useReactivateAnalytics } from './analytics';
import { trackInterestedInPriveeClicked } from '@/util/privee_analytics';
import { trackPriveeInterestExpressed } from '@/util/analytics';

export type PriveeInterestStatus = 'idle' | 'submitting' | 'error' | 'success';

export type ExpressPriveeInterestSubmitOptions = {
  smsOptIn?: boolean;
};

export function useExpressPriveeInterest() {
  const { value: user, actions: userActions } = useUser();
  const {
    value: { closet }
  } = useCloset();
  const { metadata } = useReactivateAnalytics(
    closet?.subscriptionPlanProducts,
    closet?.insurancePlanProducts
  );
  const [status, setStatus] = useState<PriveeInterestStatus>('idle');

  const submit = async (
    options: ExpressPriveeInterestSubmitOptions = {}
  ): Promise<boolean> => {
    trackInterestedInPriveeClicked(metadata);
    setStatus('submitting');
    try {
      await userActions.update({ isInterestedInPrivee: true });
      trackPriveeInterestExpressed(
        user,
        'privee-logged-in-express-interest-button'
      );
    } catch {
      setStatus('error');
      return false;
    }

    if (options.smsOptIn) {
      if (!user) {
        console.warn(
          `[Privee] Express interest in Réservé helper was called with 'smsOptIn', but no user is loaded. Skipping Braze capture call.`
        );
      } else {
        try {
          const leadCaptureResponse = await fetch(
            '/api/marketing/leadCapture',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: user.email,
                phone: user.phone,
                existingUser: {
                  id: user.id,
                  email: user.email,
                  phone: user.phone
                },
                source: 'privee_logged-in_express-interest-button',
                optInText: true,
                attributes: {
                  firstName: user.firstName,
                  lastName: user.lastName,
                  interested_in_privee: true
                }
              })
            }
          );

          if (!leadCaptureResponse.ok) {
            const errorBody = await leadCaptureResponse.text();
            console.warn('Braze opt-in failed body:', errorBody);
            throw new Error(
              `Failed to opt-in user ${user.email} for SMS: ${leadCaptureResponse.status} ${leadCaptureResponse.statusText}`
            );
          }
        } catch (error) {
          // Just log if Braze fails; do not block flow
          console.warn(error);
        }
      }
    }

    setStatus('success');
    return true;
  };

  return {
    status,
    submit,
    isAlreadyInterested: user?.isInterestedInPrivee === true
  };
}
